import { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ShieldAlert, Loader2, MailCheck, Smartphone, Trash2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import PolicyBody from "@/components/site/PolicyBody";
import { getPolicyBySlug } from "@/data/policyRegistry";
import { API, SITE } from "@/lib/site";
import useSeo from "@/hooks/useSeo";

const schema = z.object({
  email: z.string().email("Enter the email on your Orrbbit account"),
  full_name: z.string().optional(),
  reason: z.string().optional(),
  details: z.string().optional(),
  confirm: z.literal(true, { errorMap: () => ({ message: "Please confirm you understand this request" }) }),
});

const removed = [
  "Your profile, photos and display information",
  "Photo Verification images and verification status",
  "Your location, Radar and meetup sharing data",
  "Professional chats, events and reviews",
  "Professional credentials and documents you uploaded",
  "Business profiles, events and subscription records",
  "Push notification tokens and access to your account",
];

const field = "w-full rounded-xl border border-input bg-white px-4 py-3 text-navy placeholder:text-bodygrey/50 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/30";

export default function DeleteAccount() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  const policy = getPolicyBySlug("account-deletion-data-retention");
  const { register, handleSubmit, formState: { errors } } = useForm({ resolver: zodResolver(schema), defaultValues: { confirm: false } });

  useSeo({ title: "Delete Your Account — Orrbbit", description: "Request deletion of your Orrbbit account without logging in. We confirm ownership with a secure, single-use email link.", path: "/delete-account" });

  const onSubmit = async (data) => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.post(`${API}/deletion-requests`, data);
      setDone(res.data);
      toast.success("Request received", { description: "Check your inbox for a secure confirmation link." });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Please try again or email support@orrbbit.com.";
      setErr(msg);
      toast.error("Couldn't submit request", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="delete-account-page">
      <section className="border-b border-border bg-lightbg">
        <div className="container-x section-space">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full bg-red-50 px-4 py-1.5 text-xs font-semibold text-red-600"><ShieldAlert size={14} /> Account deletion</span>
            <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight text-navy md:text-5xl">Delete your Orrbbit account</h1>
            <p className="mt-4 max-w-2xl text-lg text-bodygrey">You can delete your account inside the Orrbbit app, or request deletion here without logging in. We verify ownership with a secure, single-use email link before anything is processed.</p>
          </Reveal>
        </div>
      </section>

      <div className="container-x py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal className="mb-8 rounded-2xl border border-border bg-white p-6 shadow-soft">
              <div className="flex items-center gap-2 text-navy"><Smartphone size={20} className="text-teal" /><h2 className="font-heading text-lg font-semibold">Delete inside the app</h2></div>
              <p className="mt-2 text-sm text-bodygrey">Open Orrbbit → Profile → Settings → Account → Delete Account, then follow the prompts. This is the fastest way to remove your account.</p>
            </Reveal>

            {done ? (
              <Reveal>
                <div data-testid="deletion-success" className="rounded-2xl border border-teal/30 bg-teal/5 p-8">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-teal/15 text-teal"><MailCheck size={28} /></span>
                  <h2 className="mt-5 font-heading text-2xl font-semibold text-navy">Check your inbox</h2>
                  <p className="mt-3 text-bodygrey">{done.message}</p>
                  <p className="mt-3 text-sm text-bodygrey">Reference: <span className="font-mono font-semibold text-teal" data-testid="deletion-reference">{done.reference}</span>. The confirmation link expires in 24 hours and can be used once.</p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link to="/" className="rounded-full bg-teal px-6 py-2.5 font-semibold text-white transition-transform hover:scale-105">Back to home</Link>
                    <a href={`mailto:${SITE.supportEmail}`} className="rounded-full border border-border px-6 py-2.5 font-semibold text-navy hover:bg-lightbg">Contact support</a>
                  </div>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <div className="rounded-2xl border border-border bg-white p-6 shadow-soft md:p-8">
                  <h2 className="font-heading text-lg font-semibold text-navy">Request deletion by email</h2>
                  <form onSubmit={handleSubmit(onSubmit)} data-testid="deletion-form" className="mt-5 space-y-5">
                    <div>
                      <label htmlFor="deletion-email-input" className="mb-1.5 block text-sm font-medium text-navy">Account email *</label>
                      <input id="deletion-email-input" data-testid="deletion-email-input" className={field} placeholder="you@email.com" {...register("email")} />
                      {errors.email && <p className="mt-1.5 text-xs text-red-500">{errors.email.message}</p>}
                    </div>
                    <div>
                      <label htmlFor="deletion-name-input" className="mb-1.5 block text-sm font-medium text-navy">Full name <span className="text-bodygrey">(optional)</span></label>
                      <input id="deletion-name-input" data-testid="deletion-name-input" className={field} placeholder="As it appears on your account" {...register("full_name")} />
                    </div>
                    <div>
                      <label htmlFor="deletion-details-input" className="mb-1.5 block text-sm font-medium text-navy">Reason <span className="text-bodygrey">(optional)</span></label>
                      <textarea id="deletion-details-input" data-testid="deletion-details-input" rows={3} className={`${field} resize-none`} placeholder="Anything you'd like us to know" {...register("details")} />
                    </div>
                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-lightbg p-4">
                      <input type="checkbox" data-testid="deletion-confirm-checkbox" className="mt-1 h-4 w-4 accent-teal" {...register("confirm")} />
                      <span className="text-sm text-bodygrey">I understand this will start a request to permanently delete my Orrbbit account and personal data, and that I must confirm via the email link. This action may be irreversible.</span>
                    </label>
                    {errors.confirm && <p className="-mt-3 text-xs text-red-500">{errors.confirm.message}</p>}
                    {err && <p data-testid="deletion-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{err}</p>}
                    <button type="submit" disabled={loading} data-testid="deletion-submit-btn" className="inline-flex items-center gap-2 rounded-full bg-red-500 px-8 py-3 font-semibold text-white transition-transform hover:scale-105 disabled:opacity-60">
                      {loading ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={18} />}{loading ? "Submitting..." : "Request deletion"}
                    </button>
                    <p className="text-xs text-bodygrey">For your security, we never reveal whether an account exists. If an account matches, we'll email a secure confirmation link.</p>
                  </form>
                </div>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-5">
            <Reveal>
              <div className="rounded-2xl border border-border bg-lightbg p-6">
                <h3 className="font-heading text-lg font-semibold text-navy">What gets removed</h3>
                <ul className="mt-3 space-y-2 text-sm text-bodygrey">
                  {removed.map((r) => <li key={r} className="flex gap-2"><span className="text-teal">•</span>{r}</li>)}
                </ul>
                <p className="mt-4 text-sm text-bodygrey">Some records — for example reports, safety investigations, transactions or fraud-prevention records — may be anonymised or retained where required for safety or legal reasons. Exact retention periods are being finalised and will be confirmed in the Account Deletion &amp; Data Retention Policy (also available at <Link to="/deletion-policy" className="font-semibold text-teal hover:underline">/deletion-policy</Link>) before launch.</p>
              </div>
            </Reveal>
          </div>
        </div>

        {policy && (
          <Reveal className="mt-16 border-t border-border pt-10">
            <h2 className="font-heading text-2xl font-bold text-navy">{policy.title}</h2>
            <p className="mt-1 text-sm text-bodygrey">Version {policy.version} · Effective {policy.effective_date} · Last updated {policy.last_updated}</p>
            <div className="mt-6 max-w-3xl"><PolicyBody policy={policy} /></div>
          </Reveal>
        )}
      </div>
    </div>
  );
}

